import { ArrowUpTray, ArrowUturnLeft, PencilSquare } from '@medusajs/icons';
import { Badge, Button, Input, Label, Skeleton, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { settingFileUrl } from '../settings/settings.api';
import type { Company } from './companies.api';
import {
  type CompanySetting,
  companySettingFileUrl,
  companySettingsQueryKey,
  companySettingsQueryOptions,
  effectiveValue,
  isFileSetting,
  resetCompanySetting,
  updateCompanySetting,
  uploadCompanySettingFile,
} from './company-settings.api';

/**
 * Semnătura pe documente a unei firme: ce suprascrie ea din valorile globale — imaginea
 * semnăturii, numele și calitatea semnatarului.
 *
 * Card în tabul „Setări firmă" (#207), unde a ajuns din sertarul „Semnătură" de pe rândul
 * firmei. Firma pe care se scrie e cea a ecranului — **nu firma activă din sidebar**, care
 * ar fi făcut din alegere ceva ce se uită.
 *
 * **Fiecare rând se salvează pe loc**, cu butonul lui: butonul „Salvează" al datelor firmei
 * nu le poartă, și de aceea stă pe cardul datelor, nu în antetul ecranului.
 *
 * ⚠️ **Moștenirea se spune, nu se deduce.** Un rând moștenit arată valoarea globală, dar cu
 * insigna „de la global" pe el: fără ea, o firmă fără ștampilă proprie ar arăta exact ca una
 * care și-a urcat-o — iar diferența e chiar ce vine operatorul să verifice.
 */
export function CompanySignatureSection({ company }: { company: Company }) {
  const query = useQuery(companySettingsQueryOptions(company.id));

  return (
    <SectionContainer>
      <SectionHeader
        title="Semnătura pe documente"
        subtitle="Ce lasă necompletat firma vine din setările globale."
      />

      <div className="flex flex-col gap-y-4 px-6 py-4">
        {query.isPending ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : query.error ? (
          <SectionEmpty tone="error">
            {query.error instanceof Error
              ? query.error.message
              : 'Setările firmei nu au putut fi citite.'}
          </SectionEmpty>
        ) : (
          query.data.map((setting) => (
            <CompanySettingRow key={setting.name} companyId={company.id} setting={setting} />
          ))
        )}
      </div>
    </SectionContainer>
  );
}

function CompanySettingRow({ companyId, setting }: { companyId: string; setting: CompanySetting }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(setting.value ?? '');
  const [file, setFile] = useState<File | null>(null);

  // Câmpul pornește de la ce are firma, la fiecare deschidere a formularului: altfel, după
  // o revenire la global, ar rămâne pe ecran valoarea ștearsă.
  useEffect(() => {
    if (!editing) {
      setValue(setting.value ?? '');
      setFile(null);
    }
  }, [editing, setting.value]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: companySettingsQueryKey(companyId) });

  const onError = (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : 'Salvarea a eșuat.');

  const save = useMutation({
    mutationFn: () => updateCompanySetting({ companyId, name: setting.name, value }),
    onSuccess: async () => {
      toast.success('Setarea firmei a fost salvată');
      await invalidate();
      setEditing(false);
    },
    onError,
  });

  const upload = useMutation({
    mutationFn: () => {
      if (!file) {
        throw new Error('Nu s-a ales niciun fișier.');
      }

      return uploadCompanySettingFile({ companyId, name: setting.name, file });
    },
    onSuccess: async () => {
      toast.success('Fișier încărcat');
      await invalidate();
      setEditing(false);
    },
    onError,
  });

  const reset = useMutation({
    mutationFn: () => resetCompanySetting({ companyId, name: setting.name }),
    onSuccess: async () => {
      toast.success('Firma folosește iar valoarea globală');
      await invalidate();
      setEditing(false);
    },
    onError,
  });

  const isBusy = save.isPending || upload.isPending || reset.isPending;

  return (
    <div className="flex flex-col gap-y-2 rounded-lg border border-ui-border-base p-3">
      <div className="flex items-center justify-between gap-x-2">
        <Text size="small" className="font-mono">
          {setting.name}
        </Text>
        {setting.inherited ? (
          <Badge size="2xsmall" color="grey">
            de la global
          </Badge>
        ) : (
          <Badge size="2xsmall" color="blue">
            pe firmă
          </Badge>
        )}
      </div>

      <CompanySettingValue companyId={companyId} setting={setting} />

      {editing ? (
        isFileSetting(setting) ? (
          <div className="flex flex-col gap-y-2">
            <Label htmlFor={`file-${setting.name}`}>Fișier nou</Label>
            <Input
              id={`file-${setting.name}`}
              type="file"
              // `accept` filtrează doar dialogul; formatul se verifică pe server, după
              // conținut, nu după extensie.
              accept="image/png"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              disabled={isBusy}
            />
            <Text size="small" className="text-ui-fg-subtle">
              PNG, cel mult 1 MB. Documentele deja generate rămân cu ștampila cu care au ieșit.
            </Text>
          </div>
        ) : (
          <div className="flex flex-col gap-y-2">
            <Label htmlFor={`value-${setting.name}`}>Valoarea firmei</Label>
            <Input
              id={`value-${setting.name}`}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              maxLength={255}
              disabled={isBusy}
            />
          </div>
        )
      ) : null}

      <div className="flex flex-wrap gap-2">
        {editing ? (
          <>
            <Button
              size="small"
              onClick={() => (isFileSetting(setting) ? upload.mutate() : save.mutate())}
              isLoading={save.isPending || upload.isPending}
              disabled={isBusy || (isFileSetting(setting) && file === null)}
            >
              Salvează
            </Button>
            <Button
              size="small"
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={isBusy}
            >
              Renunță
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="secondary"
            onClick={() => setEditing(true)}
            disabled={isBusy}
          >
            {isFileSetting(setting) ? <ArrowUpTray /> : <PencilSquare />}
            {isFileSetting(setting) ? 'Încarcă' : 'Modifică'}
          </Button>
        )}

        {setting.inherited ? null : (
          <Button
            size="small"
            variant="transparent"
            onClick={() => reset.mutate()}
            isLoading={reset.isPending}
            disabled={isBusy}
          >
            <ArrowUturnLeft />
            Folosește setările globale
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Ce se tipărește pentru firma asta, și de unde vine.
 *
 * Fundalul în tablă de șah la imagini: un PNG de ștampilă e transparent, iar pe alb n-ai
 * cum să spui dacă s-a încărcat o imagine goală.
 */
function CompanySettingValue({
  companyId,
  setting,
}: {
  companyId: string;
  setting: CompanySetting;
}) {
  const effective = effectiveValue(setting);

  if (effective === null) {
    return (
      <Text size="small" className="text-ui-fg-muted">
        {setting.inherited ? 'Nimic, nici pe firmă, nici global' : 'Golită dinadins pe firmă'}
      </Text>
    );
  }

  if (isFileSetting(setting)) {
    // ⚠️ Fișierul moștenit se citește de pe ruta **globală**, nu de pe cea a firmei: ruta
    // firmei nu cade pe global, tocmai ca o imagine moștenită să nu poată fi confundată cu
    // una proprie.
    const src = setting.inherited
      ? settingFileUrl({ id: null, name: setting.name, value: effective, type: setting.type })
      : companySettingFileUrl(companyId, setting);

    return (
      <img
        src={src}
        alt={setting.name}
        className="max-h-12 max-w-[160px] rounded border border-ui-border-base bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#ffffff_0%_50%)] bg-[length:12px_12px] object-contain p-1"
      />
    );
  }

  return <Text size="small">{effective}</Text>;
}
